import React, {useRef, useState} from 'react';
import * as yup from 'yup';

const TOO_MANY_DECIMALS_REGEX = /\.\d{5,}$/;

const INVALID_INPUT_MESSAGE =
  'Only positive numbers with up to 4 decimal places are allowed';

type FormikSetFieldError = (field: string, message: string | undefined) => void;
type FormikSetFieldTouched = (
  field: string,
  isTouched: boolean,
  shouldValidate: boolean,
) => void;

interface UsePositiveNumberValidationReturn {
  isInputValid: boolean;
  validateInput: (fieldName: string, maxValue?: number, customMaxMessage?: string) => (value: string) => boolean;
  resetValidation: () => void;
  connectFormik: (
    setFieldError: FormikSetFieldError,
    setFieldTouched: FormikSetFieldTouched,
  ) => void;
  handleKeyDown: (fieldName: string) => React.KeyboardEventHandler<HTMLInputElement>;
  handlePaste: (
    fieldName: string, 
    maxValue?: number, 
    customMaxMessage?: string
  ) => React.ClipboardEventHandler<HTMLInputElement>;
}

const hasTooManyDecimals = (val: string): boolean => {
  const strVal = String(val).toLowerCase();
  
  if (strVal.includes('e-')) {
    const [base, exponent] = strVal.split('e-');
    const baseDecimals = base.includes('.') ? base.split('.')[1].length : 0;
    return (baseDecimals + Number(exponent)) > 4;
  }
  
  return TOO_MANY_DECIMALS_REGEX.test(strVal);
};

export const usePositiveNumberValidation = (
  initialValid = true,
): UsePositiveNumberValidationReturn => {
  const [isInputValid, setIsInputValid] = useState<boolean>(initialValid);

  const setFieldErrorRef = useRef<FormikSetFieldError | null>(null);
  const setFieldTouchedRef = useRef<FormikSetFieldTouched | null>(null);

  const connectFormik = (
    setFieldError: FormikSetFieldError,
    setFieldTouched: FormikSetFieldTouched,
  ): void => {
    setFieldErrorRef.current = setFieldError;
    setFieldTouchedRef.current = setFieldTouched;
  };

  const showInvalidInputError = (fieldName: string, customMessage?: string): void => {
    setFieldTouchedRef.current?.(fieldName, true, false);
    setFieldErrorRef.current?.(fieldName, customMessage || INVALID_INPUT_MESSAGE);
  };

  const validateInput = (
    fieldName: string, 
    maxValue?: number, 
    customMaxMessage?: string
  ) => (value: string): boolean => {
    const trimmed = value.trim();

    if (trimmed === '' || trimmed === '.') {
      setIsInputValid(false);
      return false;
    }

    const numValue = Number(trimmed);
    const isFormatValid = !isNaN(numValue) && numValue >= 0 && !hasTooManyDecimals(trimmed);
    const isWithinRange = maxValue === undefined || numValue <= maxValue;

    const isValid = isFormatValid && isWithinRange;

    if (!isValid) {
      const msg = !isWithinRange 
          ? (customMaxMessage || `Value exceeds maximum limit (${maxValue})`) 
          : INVALID_INPUT_MESSAGE;
      showInvalidInputError(fieldName, msg);
    }

    setIsInputValid(isValid);
    return isValid;
  };

  const resetValidation = (): void => {
    setIsInputValid(true);
  };

  const handleKeyDown =
    (fieldName: string): React.KeyboardEventHandler<HTMLInputElement> =>
    (e) => {
      if (['e', 'E', '+', '-'].includes(e.key)) {
        e.preventDefault();
        showInvalidInputError(fieldName);
      }
    };

  const handlePaste =
    (fieldName: string, maxValue?: number, customMaxMessage?: string): React.ClipboardEventHandler<HTMLInputElement> =>
    (e) => {
      const pasted = e.clipboardData.getData('text');
      const trimmed = pasted.trim();
      const numValue = Number(trimmed);
      
      const isFormatInvalid = isNaN(numValue) || numValue < 0 || hasTooManyDecimals(trimmed);
      const isExceedingRange = maxValue !== undefined && numValue > maxValue;

      if (isFormatInvalid || isExceedingRange) {
        e.preventDefault();
        const msg = isExceedingRange 
            ? (customMaxMessage || `Value exceeds maximum limit (${maxValue})`) 
            : INVALID_INPUT_MESSAGE;
        showInvalidInputError(fieldName, msg);
      }
    };

  return {
    isInputValid,
    validateInput,
    resetValidation,
    connectFormik,
    handleKeyDown,
    handlePaste,
  };
};

export const getPositiveNumberYupSchema = (
  requiredMessage = 'Amount required',
  minValue = 0,
  maxValue?: number,
  customMaxMessage?: string,
) => {
  let schema = yup
    .number()
    .required(requiredMessage)
    .min(minValue, `Must be ${minValue} or greater`);

  if (maxValue !== undefined) {
    schema = schema.max(maxValue, customMaxMessage || `Must be ${maxValue} or less`);
  }

  return schema.test(
    'max-decimals',
    'Maximum 4 decimal places allowed',
    (value) => {
      if (value == null) return true;
      const strValue = String(value);
      return !hasTooManyDecimals(strValue);
    },
  );
};
